import { useState, type ComponentProps } from 'react'
import { useQuery } from '@connectrpc/connect-query'
import { create } from '@bufbuild/protobuf'
import { ChevronDown, ChevronRight, Inbox, Mail, Send, Users } from 'lucide-react'
import { listEmails } from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import { ListEmailsRequestSchema } from '@/gen/cortado/v1/emails_pb'
import { EmailMessageDirection, EmailDeliveryStatus } from '@/gen/cortado/v1/emails_pb'
import type { EmailThreadWithMessages, EmailWithAddresses } from '@/gen/cortado/v1/emails_pb'
import type { ParentTypeEnum } from '@/gen/cortado/v1/settings_pb'
import { Empty, EmptyDescription, EmptyMedia } from '@/components/ui/empty'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { formatTimestampWithTime } from '@/lib/date-utils'
import { emailAddressesOfType } from './email-addresses'
import { EmailAttachmentList } from './email-attachment-list'
import { EmailBodyFrame } from './email-body-frame'
import { ThreadReply } from './thread-reply'

interface EmailHistoryListProps {
  organizationId: string
  parentType: ParentTypeEnum
  parentId: string
}

const PAGE_LIMIT = 20

export function EmailHistoryList({ organizationId, parentType, parentId }: EmailHistoryListProps) {
  const [offset, setOffset] = useState(0)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const { data, isLoading } = useQuery(
    listEmails,
    create(ListEmailsRequestSchema, {
      organizationId,
      parentType,
      parentId,
      limit: PAGE_LIMIT,
      offset,
    })
  )

  const threads = data?.threads ?? []
  const totalCount = data?.threadTotalCount ?? data?.totalCount ?? 0

  if (isLoading) {
    return <EmailHistorySkeleton />
  }

  if (threads.length === 0) {
    return (
      <Empty className="border border-dashed py-12">
        <EmptyMedia variant="icon">
          <Mail className="h-5 w-5" />
        </EmptyMedia>
        <EmptyDescription>No emails yet</EmptyDescription>
      </Empty>
    )
  }

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {threads.map((item) => {
          const threadId = item.thread?.id ?? item.messages[0]?.email?.id ?? ''
          return (
            <EmailThreadRow
              key={threadId}
              organizationId={organizationId}
              parentType={parentType}
              parentId={parentId}
              item={item}
              isExpanded={expandedId === threadId}
              onToggle={() => setExpandedId((prev) => (prev === threadId ? null : threadId))}
            />
          )
        })}
      </div>

      <div className="flex items-center justify-between pt-2">
        <span className="text-xs text-muted-foreground">
          {offset + 1}-{Math.min(offset + threads.length, totalCount)} of {totalCount}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOffset((o) => Math.max(0, o - PAGE_LIMIT))}
            disabled={offset === 0}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOffset((o) => o + PAGE_LIMIT)}
            disabled={offset + PAGE_LIMIT >= totalCount}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  )
}

interface EmailThreadRowProps {
  organizationId: string
  parentType: ParentTypeEnum
  parentId: string
  item: EmailThreadWithMessages
  isExpanded: boolean
  onToggle: () => void
}

function EmailThreadRow({ organizationId, parentType, parentId, item, isExpanded, onToggle }: EmailThreadRowProps) {
  const thread = item.thread
  const messages = item.messages ?? []
  if (!thread) return null

  const lastMessage = messages[messages.length - 1]
  const isOutbound = thread.lastMessageDirection === EmailMessageDirection.OUTBOUND
  const timestamp = formatTimestampWithTime(thread.lastMessageAt)
  const preview = thread.lastMessagePreview || lastMessage?.preview || '(no content)'
  const participantCount = thread.participants.length
  const messageCount = thread.messageCount || messages.length

  return (
    <div className="rounded-lg border transition-colors">
      <button
        type="button"
        onClick={onToggle}
        className="w-full rounded-lg p-4 text-left transition-colors hover:bg-muted/50"
      >
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              )}
              {isOutbound ? (
                <Send className="h-4 w-4 shrink-0 text-primary" />
              ) : (
                <Inbox className="h-4 w-4 shrink-0 text-muted-foreground" />
              )}
              <span className="truncate text-sm font-semibold">
                {thread.subject || '(no subject)'}
              </span>
            </div>
            <span className="shrink-0 text-xs text-muted-foreground">{timestamp}</span>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <Users className="h-3.5 w-3.5" />
              {participantCount} {participantCount === 1 ? 'participant' : 'participants'}
            </span>
            <span>{messageCount} {messageCount === 1 ? 'message' : 'messages'}</span>
          </div>

          {!isExpanded && (
            <div className="line-clamp-1 text-sm text-muted-foreground">
              {preview}
            </div>
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="space-y-3 border-t p-3">
          {messages.map((message) => (
            <EmailMessageRow key={message.email?.id} item={message} />
          ))}
          {thread.id && (
            <ThreadReply
              organizationId={organizationId}
              parentType={parentType}
              parentId={parentId}
              threadId={thread.id}
            />
          )}
        </div>
      )}
    </div>
  )
}

interface EmailMessageRowProps {
  item: EmailWithAddresses
}

function EmailMessageRow({ item }: EmailMessageRowProps) {
  const email = item.email
  if (!email) return null

  const isOutbound = email.direction === EmailMessageDirection.OUTBOUND
  const fromAddress = emailAddressesOfType(item.addresses, 'from')[0] ?? ''
  const toAddresses = emailAddressesOfType(item.addresses, 'to').join(', ')
  const timestamp = formatTimestampWithTime(email.createdAt)
  const hasBody = email.body !== undefined && email.body !== null && email.body !== ''

  return (
    <div className={cn('rounded-md border', isOutbound ? 'bg-primary/5' : 'bg-muted/40')}>
      <div className="space-y-1 p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            {isOutbound ? (
              <Send className="h-4 w-4 shrink-0 text-primary" />
            ) : (
              <Inbox className="h-4 w-4 shrink-0 text-muted-foreground" />
            )}
            <span className="truncate text-sm text-muted-foreground">
              {fromAddress} - {toAddresses}
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <DeliveryStatusBadge
              status={email.deliveryStatus}
              bounceReason={email.bounceReason}
            />
            <span className="text-xs text-muted-foreground">{timestamp}</span>
          </div>
        </div>
      </div>
      {hasBody ? (
        <CollapsibleEmailBody html={email.body!} />
      ) : (
        <div className="border-t px-3 py-3 text-sm italic text-muted-foreground">(no content)</div>
      )}
      <EmailAttachmentList email={email} />
    </div>
  )
}

function CollapsibleEmailBody({ html }: { html: string }) {
  const [showTrimmed, setShowTrimmed] = useState(false)
  const split = splitQuotedContent(html)
  const visibleHtml = split.trimmed && !showTrimmed ? split.visible : html

  return (
    <div className="border-t">
      <EmailBodyFrame html={visibleHtml} />
      {split.trimmed && (
        <div className="border-t px-3 py-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 px-2 text-xs"
            onClick={() => setShowTrimmed((value) => !value)}
          >
            {showTrimmed ? 'Hide trimmed content' : 'Show trimmed content'}
          </Button>
        </div>
      )}
    </div>
  )
}

function splitQuotedContent(html: string): { visible: string; trimmed: boolean } {
  const markers = [
    '<blockquote',
    'class="gmail_quote"',
    "class='gmail_quote'",
    '<div class="gmail_quote"',
    '<div class="moz-cite-prefix"',
    '-----Original Message-----',
    ' wrote:',
  ]
  const lower = html.toLowerCase()
  const index = markers.reduce<number>((best, marker) => {
    const next = lower.indexOf(marker.toLowerCase())
    if (next <= 0) return best
    return best === -1 || next < best ? next : best
  }, -1)

  if (index <= 0) {
    return { visible: html, trimmed: false }
  }

  return { visible: html.slice(0, index), trimmed: true }
}

interface DeliveryStatusBadgeProps {
  status: EmailDeliveryStatus
  bounceReason?: string
}

function DeliveryStatusBadge({ status, bounceReason }: DeliveryStatusBadgeProps) {
  if (
    status === EmailDeliveryStatus.UNSPECIFIED ||
    status === EmailDeliveryStatus.PENDING
  ) {
    return null
  }

  const variant: ComponentProps<typeof Badge>['variant'] = (() => {
    switch (status) {
      case EmailDeliveryStatus.DELIVERED:
        return 'success'
      case EmailDeliveryStatus.BOUNCED:
      case EmailDeliveryStatus.COMPLAINED:
      case EmailDeliveryStatus.FAILED:
        return 'destructive'
      case EmailDeliveryStatus.SUPPRESSED:
        return 'outline'
      case EmailDeliveryStatus.UNCERTAIN:
        return 'warning'
      case EmailDeliveryStatus.SENT:
      default:
        return 'secondary'
    }
  })()

  const label = deliveryStatusLabel(status)
  const tooltipText = deliveryStatusTooltip(status, bounceReason)

  const badge = (
    <Badge variant={variant}>
      {label}
    </Badge>
  )

  if (!tooltipText) {
    return badge
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span>{badge}</span>
      </TooltipTrigger>
      <TooltipContent side="top">{tooltipText}</TooltipContent>
    </Tooltip>
  )
}

function deliveryStatusLabel(status: EmailDeliveryStatus): string {
  switch (status) {
    case EmailDeliveryStatus.SENT:
      return 'Sent'
    case EmailDeliveryStatus.DELIVERED:
      return 'Delivered'
    case EmailDeliveryStatus.BOUNCED:
      return 'Bounced'
    case EmailDeliveryStatus.COMPLAINED:
      return 'Complained'
    case EmailDeliveryStatus.FAILED:
      return 'Failed'
    case EmailDeliveryStatus.SUPPRESSED:
      return 'Suppressed'
    case EmailDeliveryStatus.UNCERTAIN:
      return 'May have been sent'
    default:
      return ''
  }
}

function deliveryStatusTooltip(
  status: EmailDeliveryStatus,
  bounceReason?: string,
): string | null {
  switch (status) {
    case EmailDeliveryStatus.BOUNCED:
      return bounceReason && bounceReason.trim().length > 0
        ? bounceReason
        : 'The recipient mail server rejected this message.'
    case EmailDeliveryStatus.COMPLAINED:
      return 'Recipient reported as spam'
    case EmailDeliveryStatus.SUPPRESSED:
      return 'Recipient is on the suppression list; this message was not sent.'
    case EmailDeliveryStatus.FAILED:
      return 'Delivery failed.'
    case EmailDeliveryStatus.UNCERTAIN:
      return 'We couldn\'t confirm whether this message reached the recipient — it may have been delivered.'
    default:
      return null
  }
}

function EmailHistorySkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="rounded-lg border p-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Skeleton className="h-4 w-4" />
                <Skeleton className="h-4 w-24" />
              </div>
              <Skeleton className="h-3 w-32" />
            </div>
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-16 w-full" />
          </div>
        </div>
      ))}
    </div>
  )
}
